/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Guest-molecule library for the crystal chamber.
 *
 * A "guest" is a small molecule inserted into a crystal supercell that
 * is NOT part of the periodic basis — a clathrate guest (methane in an
 * ice cage), a dopant, a solute. The host crystal tiles its basis; a
 * guest is a one-off, non-periodic insertion (see CrystalGuest in
 * types.ts and the append step in buildCrystalScaffold).
 *
 * Each library entry is SELF-PARAMETERIZED: it carries its own per-site
 * Lennard-Jones σ/ε and partial charge, so the host crystal's force
 * field need not know about guest species — it configures the host and
 * skips guest atoms (Atom.guestId). buildGuestAtoms() stamps those
 * parameters straight onto the Atom objects it builds.
 *
 * Geometry is in Å, in a molecule-local frame centered on the molecule;
 * buildGuestAtoms() applies the guest's placement transform. LJ ε is in
 * eV (the engine's unit), converted from the literature ε/k_B in kelvin
 * (ε[eV] = (ε/k_B)[K] · 8.617333e-5). The optional per-site `mass`
 * override supports united-atom sites (one site standing for a group).
 */
import { Euler, Vector3 } from 'three';
import { Atom } from '../Atom.ts';
import { CrystalGuest, CrystalGuestSpecies, MoleculeTransform } from '../../types.ts';
import { shiftedForceLJ } from './PairPotential.ts';
import { getUffLj } from '../uffParameters.ts';
import { PeriodicBox } from './PeriodicBox.ts';

/** One interaction site of a guest molecule, in the molecule-local frame. */
interface GuestSite {
  element: string;
  x: number; // Å
  y: number;
  z: number;
  sigma: number; // Å — Lennard-Jones diameter
  epsilon: number; // eV — Lennard-Jones well depth
  charge: number; // e — partial charge (0 for nonpolar guests)
  /** Override the element mass — for united-atom sites (e.g. methane). */
  mass?: number;
}

/** A guest species: its sites and intramolecular bonds (local index pairs). */
export interface GuestSpecies {
  key: string;
  name: string;
  sites: GuestSite[];
  bonds: [number, number][];
}

// k_B in eV/K, for the ε/k_B → eV conversions noted per entry.
const KB_EV = 8.617333e-5;

/**
 * The built-in guest library — small and conservative for now: the
 * pedagogically interesting clathrate / dopant guests, each with
 * literature force-field parameters. Multi-site guests are treated as
 * rigid bodies (see configureCrystalDynamics).
 */
export const GUEST_LIBRARY: Record<string, GuestSpecies> = {
  Ar: {
    key: 'Ar',
    name: 'Argon',
    sites: [{ element: 'Ar', x: 0, y: 0, z: 0, sigma: 3.4, epsilon: 120 * KB_EV, charge: 0 }],
    bonds: [],
  },
  Kr: {
    key: 'Kr',
    name: 'Krypton',
    sites: [{ element: 'Kr', x: 0, y: 0, z: 0, sigma: 3.65, epsilon: 164 * KB_EV, charge: 0 }],
    bonds: [],
  },
  Xe: {
    key: 'Xe',
    name: 'Xenon',
    sites: [{ element: 'Xe', x: 0, y: 0, z: 0, sigma: 3.96, epsilon: 224 * KB_EV, charge: 0 }],
    bonds: [],
  },
  CH4: {
    key: 'CH4',
    name: 'Methane',
    // All-atom geometry — tetrahedral CH₄, C–H = 1.09 Å — so the four
    // hydrogens are visible. Methane's Lennard-Jones (TraPPE-UA) sits on
    // the carbon; the hydrogens are LJ-inert (a tiny σ, the same
    // placeholder the water models use for H) so the intramolecular
    // H···H pairs — which the pair kernel does not exclude — add no
    // spurious repulsion. Nonpolar: all charges zero.
    sites: [
      { element: 'C', x: 0, y: 0, z: 0, sigma: 3.73, epsilon: 148 * KB_EV, charge: 0 },
      { element: 'H', x: 0.6293, y: 0.6293, z: 0.6293, sigma: 0.4, epsilon: 0.0001, charge: 0 },
      { element: 'H', x: 0.6293, y: -0.6293, z: -0.6293, sigma: 0.4, epsilon: 0.0001, charge: 0 },
      { element: 'H', x: -0.6293, y: 0.6293, z: -0.6293, sigma: 0.4, epsilon: 0.0001, charge: 0 },
      { element: 'H', x: -0.6293, y: -0.6293, z: 0.6293, sigma: 0.4, epsilon: 0.0001, charge: 0 },
    ],
    bonds: [
      [0, 1],
      [0, 2],
      [0, 3],
      [0, 4],
    ],
  },
  N2: {
    key: 'N2',
    name: 'Nitrogen',
    // Two LJ sites, N–N = 1.10 Å. Nonpolar here — the quadrupole (a
    // 3-charge TraPPE refinement) is omitted in this first cut.
    sites: [
      { element: 'N', x: 0, y: 0, z: -0.55, sigma: 3.31, epsilon: 36 * KB_EV, charge: 0 },
      { element: 'N', x: 0, y: 0, z: 0.55, sigma: 3.31, epsilon: 36 * KB_EV, charge: 0 },
    ],
    bonds: [[0, 1]],
  },
  CO2: {
    key: 'CO2',
    name: 'Carbon dioxide',
    // EPM2: linear O=C=O, C–O = 1.149 Å, charges sum to zero.
    sites: [
      { element: 'C', x: 0, y: 0, z: 0, sigma: 2.757, epsilon: 28.129 * KB_EV, charge: 0.6512 },
      { element: 'O', x: 0, y: 0, z: -1.149, sigma: 3.033, epsilon: 80.507 * KB_EV, charge: -0.3256 },
      { element: 'O', x: 0, y: 0, z: 1.149, sigma: 3.033, epsilon: 80.507 * KB_EV, charge: -0.3256 },
    ],
    bonds: [
      [0, 1],
      [0, 2],
    ],
  },
  O2: {
    key: 'O2',
    name: 'Oxygen',
    // Two LJ sites, O–O = 1.21 Å. Nonpolar (the quadrupole is omitted,
    // as for N₂). The two O are directly bonded, so the pair kernel
    // excludes their intramolecular LJ — the sites carry full σ/ε.
    sites: [
      { element: 'O', x: 0, y: 0, z: -0.605, sigma: 3.02, epsilon: 49 * KB_EV, charge: 0 },
      { element: 'O', x: 0, y: 0, z: 0.605, sigma: 3.02, epsilon: 49 * KB_EV, charge: 0 },
    ],
    bonds: [[0, 1]],
  },
  H2: {
    key: 'H2',
    name: 'Hydrogen',
    // Two LJ sites, H–H = 0.74 Å. Nonpolar. The two H are directly
    // bonded, so their intramolecular LJ is excluded by the pair kernel.
    sites: [
      { element: 'H', x: 0, y: 0, z: -0.37, sigma: 2.6, epsilon: 17 * KB_EV, charge: 0 },
      { element: 'H', x: 0, y: 0, z: 0.37, sigma: 2.6, epsilon: 17 * KB_EV, charge: 0 },
    ],
    bonds: [[0, 1]],
  },
  NH3: {
    key: 'NH3',
    name: 'Ammonia',
    // Trigonal-pyramidal NH₃: N–H = 1.012 Å, H–N–H = 106.7°, shifted so
    // the site centroid is at the origin. Polar (q sums to zero). The LJ
    // sits on N; the H are LJ-inert (tiny σ) — the intramolecular H···H
    // pairs are 1-3, which the pair kernel does not exclude.
    sites: [
      { element: 'N', x: 0, y: 0, z: 0.2858, sigma: 3.42, epsilon: 85.5 * KB_EV, charge: -1.02 },
      { element: 'H', x: 0.9375, y: 0, z: -0.0953, sigma: 0.4, epsilon: 0.0001, charge: 0.34 },
      { element: 'H', x: -0.4688, y: 0.8119, z: -0.0953, sigma: 0.4, epsilon: 0.0001, charge: 0.34 },
      { element: 'H', x: -0.4688, y: -0.8119, z: -0.0953, sigma: 0.4, epsilon: 0.0001, charge: 0.34 },
    ],
    bonds: [
      [0, 1],
      [0, 2],
      [0, 3],
    ],
  },
  H2S: {
    key: 'H2S',
    name: 'Hydrogen sulfide',
    // Bent H₂S: S–H = 1.34 Å, H–S–H = 92.1°, shifted so the site
    // centroid is at the origin. Polar (q sums to zero). The LJ sits on
    // S; the H are LJ-inert (tiny σ) — the intramolecular H···H pair is
    // 1-3, which the pair kernel does not exclude.
    sites: [
      { element: 'S', x: 0, y: 0, z: -0.6197, sigma: 3.73, epsilon: 250 * KB_EV, charge: -0.4 },
      { element: 'H', x: 0.9652, y: 0, z: 0.3098, sigma: 0.4, epsilon: 0.0001, charge: 0.2 },
      { element: 'H', x: -0.9652, y: 0, z: 0.3098, sigma: 0.4, epsilon: 0.0001, charge: 0.2 },
    ],
    bonds: [
      [0, 1],
      [0, 2],
    ],
  },
};

/** Guest species for a UI picker — `{ key, name }` in library order. */
export const guestSpeciesList = (): { key: string; name: string }[] =>
  Object.values(GUEST_LIBRARY).map((g) => ({ key: g.key, name: g.name }));

/**
 * Resolve a guest's species: the inline `customSpecies` (a pasted cluster)
 * takes precedence over the built-in library. Returns undefined only when a
 * library key is unknown and there's no inline geometry. Every species lookup
 * goes through here so library and pasted guests behave identically downstream.
 */
export const resolveGuestSpecies = (guest: CrystalGuest): GuestSpecies | undefined =>
  guest.customSpecies ?? GUEST_LIBRARY[guest.species];

/**
 * Build a self-contained guest species from a copied crystal cluster — the
 * Paste payload. Sites are in the cluster's centroid-relative frame (Å);
 * Lennard-Jones σ/ε come from UFF for each element (so the host force field can
 * skip the cluster like any guest), and per-site charge is carried through.
 * `bonds` are local index pairs (for rendering and the flexible-host harmonic
 * path; a multi-atom cluster is also rigid-body constrained on rigid/atomic
 * hosts via Atom.guestId, independent of this list).
 */
export const makeClusterGuestSpecies = (
  key: string,
  atoms: { elementSymbol: string; offset: Vector3; charge?: number }[],
  bonds: [number, number][],
): CrystalGuestSpecies => {
  const sites = atoms.map((a) => {
    const uff = getUffLj(a.elementSymbol);
    return {
      element: a.elementSymbol,
      x: a.offset.x,
      y: a.offset.y,
      z: a.offset.z,
      // Fallbacks for an element UFF doesn't cover: a mid-range σ and a weak ε
      // so the guest is inert-but-present rather than crashing the kernel.
      sigma: uff ? uff.sigma : 3.0,
      epsilon: uff ? uff.epsilon : 0.005,
      charge: a.charge ?? 0,
    };
  });
  return { key, name: `Cluster (${atoms.length} ${atoms.length === 1 ? 'atom' : 'atoms'})`, sites, bonds };
};

/**
 * Build the Atom objects (and intramolecular bond index pairs) for one
 * placed guest. Atoms are created at their final chamber-frame position
 * — the guest's transform (origin-centered frame) applied to each
 * library site — with σ/ε/charge/mass stamped from the library and
 * `guestId` set. `startIndex` is the atom index the first site gets;
 * the returned `bonds` are LOCAL pairs (0-based within the guest), so
 * the caller offsets them by `startIndex`.
 */
export const buildGuestAtoms = (
  guest: CrystalGuest,
  startIndex: number,
): { atoms: Atom[]; bonds: [number, number][] } => {
  const spec = resolveGuestSpecies(guest);
  if (!spec) return { atoms: [], bonds: [] };
  const e = guest.transform.euler;
  const euler = new Euler(e[0] ?? 0, e[1] ?? 0, e[2] ?? 0);
  const origin = new Vector3(guest.transform.x, guest.transform.y, guest.transform.z);
  const atoms: Atom[] = [];
  for (let k = 0; k < spec.sites.length; k++) {
    const s = spec.sites[k];
    const pos = new Vector3(s.x, s.y, s.z).applyEuler(euler).add(origin);
    const atom = new Atom(startIndex + k, s.element, pos);
    atom.sigma = s.sigma;
    atom.epsilon = s.epsilon;
    atom.charge = s.charge;
    if (s.mass !== undefined) atom.mass = s.mass;
    atom.guestId = guest.id;
    atoms.push(atom);
  }
  return { atoms, bonds: spec.bonds.map(([i, j]) => [i, j] as [number, number]) };
};

/**
 * Rigid-body relax a guest's placement into the nearest clear pocket,
 * minimizing the guest–host (and guest–guest) van der Waals energy — the
 * SAME shifted-force LJ the MD kernel's guest overlay uses (host σ/ε from
 * UFF, guest σ/ε from the library). The guest is treated as a rigid body:
 * only its 6 placement DOF (3 translation + 3 rotation) move; the host
 * stays frozen. This is what turns "drop a guest and it clashes" into "drop
 * a guest and it settles into the cage", and it makes the clash halo go
 * cyan without the user hand-dragging.
 *
 * Steepest descent with a numerical gradient over the 6 DOF and a
 * backtracking line search. The gradient is cheap — a guest has a handful
 * of atoms and only the host atoms within a shell of it matter — so a few
 * hundred iterations run instantly. Returns the relaxed transform (the
 * caller writes it back to chamberGuests); on any degeneracy (unknown
 * species, no neighbors) it returns the input transform unchanged.
 *
 * `allAtoms` is the live engine atom list (host + every guest); atoms of
 * THIS guest are excluded. `box` enables minimum-image distances for a
 * guest near a cell boundary (omit for a finite cluster).
 */
export const relaxGuestPlacement = (guest: CrystalGuest, allAtoms: Atom[], box?: PeriodicBox): MoleculeTransform => {
  const spec = resolveGuestSpecies(guest);
  const start: MoleculeTransform = {
    x: guest.transform.x,
    y: guest.transform.y,
    z: guest.transform.z,
    euler: [guest.transform.euler[0] ?? 0, guest.transform.euler[1] ?? 0, guest.transform.euler[2] ?? 0],
  };
  if (!spec) return start;

  // Prefilter the neighbors that can interact with the guest: not removed,
  // not part of this guest, carrying a usable LJ ε (host → UFF; other guest
  // → its library σ/ε), and within a generous shell of the guest's start
  // (the relax only nudges it locally, so a fixed shell never goes stale).
  const SHELL = 12; // Å
  const origin0 = new Vector3(start.x, start.y, start.z);
  const neighbors: { pos: Vector3; sigma: number; eps: number }[] = [];
  const d = new Vector3();
  for (const a of allAtoms) {
    if (a.removed) continue;
    if (a.guestId !== undefined && a.guestId === guest.id) continue;
    let sigma: number;
    let eps: number;
    if (a.guestId !== undefined) {
      sigma = a.sigma;
      eps = a.epsilon;
    } else {
      const uff = getUffLj(a.elementSymbol);
      if (!uff) continue;
      sigma = uff.sigma;
      eps = uff.epsilon;
    }
    if (eps <= 0) continue;
    d.subVectors(a.position, origin0);
    if (box) box.minimumImage(d);
    if (d.lengthSq() > SHELL * SHELL) continue;
    neighbors.push({ pos: a.position, sigma, eps });
  }
  if (neighbors.length === 0) return start;

  const sites = spec.sites.map((s) => ({ local: new Vector3(s.x, s.y, s.z), sigma: s.sigma, eps: s.epsilon }));

  // Total guest–host LJ at a candidate placement p = [tx,ty,tz, rx,ry,rz].
  const euler = new Euler();
  const origin = new Vector3();
  const pos = new Vector3();
  const dv = new Vector3();
  const RCUT_REDUCED_SQ = 4; // (2σ)² in reduced units — same as the kernel overlay
  const energyOf = (p: number[]): number => {
    euler.set(p[3], p[4], p[5]);
    origin.set(p[0], p[1], p[2]);
    let E = 0;
    for (const st of sites) {
      if (st.eps <= 0) continue;
      pos.copy(st.local).applyEuler(euler).add(origin);
      for (const nb of neighbors) {
        dv.subVectors(pos, nb.pos);
        if (box) box.minimumImage(dv);
        const rsq = dv.lengthSq();
        if (rsq < 1e-10) return Infinity; // exactly coincident — reject
        const sigmaMean = 0.5 * (st.sigma + nb.sigma);
        const rCutSq = RCUT_REDUCED_SQ * sigmaMean * sigmaMean;
        if (rsq >= rCutSq) continue;
        E += shiftedForceLJ(sigmaMean, Math.sqrt(st.eps * nb.eps), rsq, rCutSq).energy;
      }
    }
    return E;
  };

  const p = [start.x, start.y, start.z, start.euler[0], start.euler[1], start.euler[2]];
  let E = energyOf(p);
  let step = 0.3; // Å (and rad) per descent move
  const h = 1e-3;
  const g = [0, 0, 0, 0, 0, 0];
  for (let iter = 0; iter < 200; iter++) {
    // Numerical gradient over the 6 rigid DOF.
    for (let k = 0; k < 6; k++) {
      const p0 = p[k];
      p[k] = p0 + h;
      const ep = energyOf(p);
      p[k] = p0 - h;
      const em = energyOf(p);
      p[k] = p0;
      g[k] = (ep - em) / (2 * h);
    }
    const gn = Math.hypot(g[0], g[1], g[2], g[3], g[4], g[5]);
    if (gn < 1e-4) break; // at a local minimum (the bound pocket)
    // Backtracking line search along −∇E (fixed-length unit-direction step).
    let moved = false;
    for (let ls = 0; ls < 10; ls++) {
      const trial = p.map((v, k) => v - (step * g[k]) / gn);
      const Et = energyOf(trial);
      if (Et < E - 1e-9) {
        for (let k = 0; k < 6; k++) p[k] = trial[k];
        E = Et;
        step = Math.min(step * 1.3, 0.6); // grow when making progress
        moved = true;
        break;
      }
      step *= 0.5;
    }
    if (!moved && step < 1e-4) break; // can't improve — done
  }

  return { x: p[0], y: p[1], z: p[2], euler: [p[3], p[4], p[5]] };
};

/**
 * Find a roomy spot for a new guest: the point inside the atom cloud
 * farthest from every atom — the largest interior void (an ice cage, an
 * interstitial site). A coarse grid search over the inset bounding box;
 * cheap, and good enough to drop a guest into a cavity instead of on top
 * of a host atom. Returns the cloud center if there are no atoms.
 *
 * The grid is inset from the bounding box so the winner is a genuine
 * interior void, not a box face (which is "empty" only on one side).
 */
// `accept`, when given, restricts the search to grid points it approves (e.g.
// the lower-Z half for "bottom"). The emptiest accepted void is returned, with
// a fallback to the global emptiest void if the predicate excluded every
// sampled point. Without `accept` the behavior is unchanged.
// `box`: measure emptiness under minimum image. Without it a sheared
// (hexagonal / triclinic) supercell fooled the search: the corners of the
// atoms' bounding box lie OUTSIDE the cell, read as empty against the drawn
// atoms, and coincide with a periodic image of the framework — a CO₂ dropped
// there sat 0.66 Å from an image oxygen and blew the lab up when played
// (chabazite 2×2×1, 2026-10-16). Under PBC every point is interior, so the
// inset that keeps a finite cluster's search off its faces is dropped too.
export const largestVoid = (atoms: Atom[], accept?: (p: Vector3) => boolean, box?: PeriodicBox): Vector3 => {
  const live = atoms.filter((a) => !a.removed);
  if (live.length === 0) return new Vector3();
  const min = new Vector3(Infinity, Infinity, Infinity);
  const max = new Vector3(-Infinity, -Infinity, -Infinity);
  for (const a of live) {
    min.min(a.position);
    max.max(a.position);
  }
  const span = new Vector3().subVectors(max, min);
  const inset = box ? 0 : 0.2;
  const lo = min.clone().addScaledVector(span, inset);
  const hi = max.clone().addScaledVector(span, -inset);
  const dv = new Vector3();
  const N = 10; // grid steps per axis (N+1 samples)
  const best = lo.clone().add(hi).multiplyScalar(0.5);
  let bestD2 = -1;
  // Emptiest void within the region predicate, tracked alongside the global one.
  const bestInRegion = best.clone();
  let bestInRegionD2 = -1;
  const p = new Vector3();
  for (let ix = 0; ix <= N; ix++) {
    for (let iy = 0; iy <= N; iy++) {
      for (let iz = 0; iz <= N; iz++) {
        p.set(lo.x + ((hi.x - lo.x) * ix) / N, lo.y + ((hi.y - lo.y) * iy) / N, lo.z + ((hi.z - lo.z) * iz) / N);
        const inRegion = !accept || accept(p);
        // Prune once this point can't beat any best still being tracked. For an
        // accepted point that's min(global, region best); else just the global.
        const threshold = inRegion ? Math.min(bestD2, bestInRegionD2) : bestD2;
        // Nearest-atom distance² at this point.
        let nearest = Infinity;
        for (const a of live) {
          let d2: number;
          if (box) {
            dv.subVectors(p, a.position);
            box.minimumImage(dv);
            d2 = dv.lengthSq();
          } else d2 = p.distanceToSquared(a.position);
          if (d2 < nearest) nearest = d2;
          if (nearest <= threshold) break;
        }
        if (nearest > bestD2) {
          bestD2 = nearest;
          best.copy(p);
        }
        if (inRegion && nearest > bestInRegionD2) {
          bestInRegionD2 = nearest;
          bestInRegion.copy(p);
        }
      }
    }
  }
  return accept && bestInRegionD2 >= 0 ? bestInRegion : best;
};

/**
 * The emptiest point along a line — where Paste drops a copy in the crystal
 * lab, which has no reference plane to click on: the line runs from the
 * camera through the right-clicked pixel. The search covers the part of the
 * line inside the atoms' bounding box (padded 1 Å), from `tStart` on, so a
 * click on an atom means "here or behind it", never the layers in front.
 * `points` are where the atoms are drawn; `skip` drops atoms that are not
 * there (cut). Emptiness is the distance to the nearest atom, under minimum
 * image when `box` is given. Returns the point, that clearance, and the index
 * of the nearest atom (to carry the point into another frame); null when the
 * line misses the atoms.
 */
export const emptiestPointOnRay = (
  points: Vector3[],
  skip: (i: number) => boolean,
  origin: Vector3,
  direction: Vector3,
  tStart: number,
  box?: PeriodicBox,
): { point: Vector3; clearance: number; nearest: number } | null => {
  const min = new Vector3(Infinity, Infinity, Infinity);
  const max = new Vector3(-Infinity, -Infinity, -Infinity);
  const live: number[] = [];
  for (let i = 0; i < points.length; i++) {
    if (skip(i)) continue;
    live.push(i);
    min.min(points[i]);
    max.max(points[i]);
  }
  if (live.length === 0) return null;
  min.subScalar(1);
  max.addScalar(1);
  // Where the line enters and leaves the box (slab method).
  let t0 = -Infinity;
  let t1 = Infinity;
  for (const k of ['x', 'y', 'z'] as const) {
    const o = origin[k];
    const d = direction[k];
    if (Math.abs(d) < 1e-12) {
      if (o < min[k] || o > max[k]) return null;
      continue;
    }
    const ta = (min[k] - o) / d;
    const tb = (max[k] - o) / d;
    t0 = Math.max(t0, Math.min(ta, tb));
    t1 = Math.min(t1, Math.max(ta, tb));
  }
  t0 = Math.max(t0, tStart, 0);
  if (!(t1 > t0)) return null;
  const steps = Math.min(1000, Math.max(1, Math.ceil((t1 - t0) / 0.2)));
  const p = new Vector3();
  const dv = new Vector3();
  let best: { point: Vector3; clearance: number; nearest: number } | null = null;
  for (let s = 0; s <= steps; s++) {
    p.copy(origin).addScaledVector(direction, t0 + ((t1 - t0) * s) / steps);
    let d2min = Infinity;
    let nearest = -1;
    for (const i of live) {
      dv.subVectors(p, points[i]);
      if (box) box.minimumImage(dv);
      const d2 = dv.lengthSq();
      if (d2 < d2min) {
        d2min = d2;
        nearest = i;
      }
    }
    const clearance = Math.sqrt(d2min);
    if (!best || clearance > best.clearance) best = { point: p.clone(), clearance, nearest };
  }
  return best;
};

/**
 * A placed guest must keep at least this much room (Å) between any of its
 * sites and every other atom, periodic images included. Closer than this the
 * overlay's repulsion ejects it with tens of eV and a free framework melts.
 */
export const MIN_GUEST_CLEARANCE = 2.0;

/**
 * Closest approach (Å) of a guest's sites to every other live atom (host and
 * other guests), under minimum image when `box` is given. Infinity when
 * there is nothing to measure against or the species is unknown.
 */
export const guestClearance = (guest: CrystalGuest, allAtoms: Atom[], box?: PeriodicBox): number => {
  const spec = resolveGuestSpecies(guest);
  if (!spec) return Infinity;
  const t = guest.transform;
  const euler = new Euler(t.euler[0] ?? 0, t.euler[1] ?? 0, t.euler[2] ?? 0);
  const origin = new Vector3(t.x, t.y, t.z);
  const sites = spec.sites.map((s) => new Vector3(s.x, s.y, s.z).applyEuler(euler).add(origin));
  const d = new Vector3();
  let best = Infinity;
  for (const a of allAtoms) {
    if (a.removed || (a.guestId !== undefined && a.guestId === guest.id)) continue;
    for (const p of sites) {
      d.subVectors(p, a.position);
      if (box) box.minimumImage(d);
      const r = d.length();
      if (r < best) best = r;
    }
  }
  return best;
};
